import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Loader2, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";

export const DECISION_ACTIONS = [
  "Approve for Investigation", "Include in Budget", "Defer",
  "Already Replaced", "Not Required", "Request GBE Inspection",
];

const STATUS_STYLE = {
  "Approved for Replacement": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Recommended for Renewal Planning": "bg-amber-50 text-amber-700 border-amber-200",
  "Identified for Assessment": "bg-slate-100 text-slate-600 border-slate-200",
};

export function DecisionBadge({ status, decision }) {
  return (
    <span className="inline-flex flex-col items-start gap-0.5">
      <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] font-medium whitespace-nowrap ${STATUS_STYLE[status] || STATUS_STYLE["Identified for Assessment"]}`}>
        {status || "Identified for Assessment"}
      </span>
      {decision && <span className="text-[10px] text-slate-400 pl-1">Client: {decision}</span>}
    </span>
  );
}

export function AssetDecisionDialog({ asset, onOpenChange, onSaved }) {
  const [action, setAction] = useState("");
  const [comment, setComment] = useState("");
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const assetId = asset?.asset_id || asset?.id;

  useEffect(() => {
    if (!asset) return;
    setAction(asset.client_decision || "");
    setComment("");
    api.get("/capital/decisions", { params: { asset_id: assetId } })
      .then((r) => setHistory(r.data.items || [])).catch(() => setHistory([]));
    // eslint-disable-next-line
  }, [assetId]);

  const save = async () => {
    if (!action) return toast.error("Pick a decision action first");
    setBusy(true);
    try {
      const r = await api.post(`/capital/assets/${encodeURIComponent(assetId)}/decision`, { action, comment });
      toast.success(`Decision recorded — ${action}`);
      onSaved?.(r.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to record decision");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!asset} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]" data-testid="asset-decision-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <ClipboardCheck className="w-4 h-4 text-gbe" /> Record decision
          </DialogTitle>
          <DialogDescription className="truncate">
            {asset?.asset_name} · {asset?.site_name}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <DecisionBadge status={asset?.decision_status} decision={asset?.client_decision} />
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger data-testid="decision-action-select"><SelectValue placeholder="Choose an action…" /></SelectTrigger>
            <SelectContent>
              {DECISION_ACTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
          <Textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)}
                    placeholder="Optional comment — context for this decision (kept in the audit trail)"
                    data-testid="decision-comment-input" />
          <p className="text-[11px] text-slate-400">
            A recommendation is not an approval — decisions here feed next year's planning and are visible to GBE.
          </p>
          <div className="flex gap-2">
            <Button className="bg-gbe hover:bg-gbe-hover text-white" onClick={save} disabled={busy}
                    data-testid="decision-save-btn">
              {busy && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />} Save decision
            </Button>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          </div>
          {history.length > 0 && (
            <div className="border-t border-slate-100 pt-3 max-h-40 overflow-y-auto space-y-2" data-testid="decision-history">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Decision history</div>
              {history.map((h, i) => (
                <div key={i} className="text-xs">
                  <span className="font-medium text-slate-800">{h.action}</span>
                  <span className="text-slate-400"> — {h.decided_by} · {(h.created_at || "").slice(0, 10)}</span>
                  {h.previous_decision && <span className="text-slate-400"> (was: {h.previous_decision})</span>}
                  {h.comment && <div className="text-slate-600 mt-0.5">{h.comment}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
